import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Info } from 'lucide-react';
import { Navbar } from '../../components/common/Navbar';
import { Footer } from '../../components/common/Footer';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { SectionHeader } from '../../components/common/SectionHeader';
import { CASES_DATA, COMPANY_INFO } from '../../data/siteData';
import { SEO } from '../../components/common/SEO';

export default function CasesIndexPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('すべて');

  const categories = ['すべて', '設計・施工実績', '運用事例・実測', 'シミュレーション'];

  const filteredCases =
    selectedCategory === 'すべて'
      ? CASES_DATA
      : CASES_DATA.filter((c) => c.category === selectedCategory);

  return (
    <div className="min-h-screen flex flex-col bg-white text-navy">
      <SEO
        title="実績・事例・導入イメージ｜設計施工・現場運用・試算"
        description="工場・物流倉庫・商業施設などにおける太陽光設計・施工実績、現場での協調制御実測事例、シミュレーションをご紹介します。"
        canonicalPath="/cases"
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-hero-gradient border-b border-surface-border py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <Badge variant="cyan" className="mb-4">CASES & SIMULATIONS</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy tracking-tight leading-tight">
              実績・事例・導入イメージ
            </h1>
            <p className="mt-5 text-base sm:text-lg text-navy-muted leading-relaxed max-w-3xl">
              創業以来の太陽光発電・設備の「設計・施工実績」、現場での「運用事例・実測」、および代表的な施設条件に基づく「シミュレーション（試算例）」を、それぞれの種別と前提条件を明示して掲載しています。
            </p>
          </div>
        </section>

        {/* Cumulative Track Record Summary (Clearly Distinguishing Installation vs Design) */}
        <section className="py-12 bg-surface-slate border-b border-surface-border">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {COMPANY_INFO.trackRecordSummary.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl border border-surface-border p-6 shadow-soft"
                >
                  <div className="text-xs font-bold text-spaq-cyan-dark mb-1">
                    {item.label}
                  </div>
                  <div className="text-3xl font-bold text-navy font-mono-num">
                    {item.value}
                    <span className="text-base font-normal text-navy-muted ml-1.5">
                      {item.unit}
                    </span>
                  </div>
                  <div className="text-[11px] text-navy-muted mt-2 leading-relaxed">
                    {item.note}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filter & Case Cards */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Filter Tabs */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                      selectedCategory === cat
                        ? 'bg-navy text-white shadow-soft'
                        : 'bg-surface-slate text-navy-muted hover:text-navy border border-surface-border'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1.5 text-xs text-navy-muted">
                <Info className="w-4 h-4 text-spaq-cyan-dark shrink-0" />
                <span>各カードに「実績」「運用実測」「シミュレーション」の区分を明記しています</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
              {filteredCases.map((item) => (
                <Link
                  key={item.slug}
                  to={`/cases/${item.slug}`}
                  className="group rounded-3xl border border-surface-border bg-white p-7 shadow-soft hover:shadow-soft-lg hover:border-spaq-cyan/50 transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <Badge variant={item.badgeColor}>{item.category}</Badge>
                    </div>
                    <div className="text-xs font-semibold text-navy-muted mb-1.5">
                      {item.facilityType}
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-navy group-hover:text-spaq-cyan-dark transition-colors leading-snug mb-3">
                      {item.title}
                    </h2>
                    <p className="text-xs sm:text-sm text-navy-muted leading-relaxed mb-6">
                      {item.summary}
                    </p>
                  </div>

                  <div>
                    <div className="space-y-2 pt-4 border-t border-surface-border mb-4">
                      {item.outcomes.map((out, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between bg-surface-slate rounded-xl px-3.5 py-2"
                        >
                          <span className="text-xs text-navy-muted">{out.label}</span>
                          <span className="text-sm font-bold text-navy">{out.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-[11px] text-navy-muted mb-4 line-clamp-2">
                      {item.conditionsNote}
                    </div>
                    <div className="inline-flex items-center gap-1 text-xs font-semibold text-spaq-cyan-dark group-hover:translate-x-1 transition-transform">
                      <span>前提条件・詳細を見る</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-16 rounded-3xl bg-surface-sky border border-spaq-cyan/30 p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="space-y-2 max-w-2xl">
                <h3 className="text-xl sm:text-2xl font-bold text-navy">
                  貴社の施設条件に合わせた個別シミュレーションを承ります
                </h3>
                <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                  屋根面積や月々の電気料金明細、30分デマンドデータをもとに、太陽光単体での導入効果や、SPAQ CORE・蓄電池を組み合わせた場合の比較試算を作成いたします。
                </p>
              </div>
              <Button
                to="/contact"
                variant="primary"
                size="lg"
                className="shrink-0"
                icon={<ArrowRight className="w-4 h-4" />}
              >
                自社施設の試算・相談をする
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
